import { useRef, type ReactNode, type RefObject } from "react";
import { AccentPicker, FormRow } from "@omnabase/ui";
import { SIGNATURE_MAX, SIGNATURE_PLACEHOLDERS, SIGNATURE_TEXT_COLOR, SIGNATURE_TEXT_PRESETS } from "@omnabase/shared";
import { ACCENTS, contrast } from "../../lib/accent";

/** The signature's pieces (the top bar, signature and reports round, 2026-10-26; brief rule 3): the two-column block of Projects › General's
 *  "Sender and signature" and Settings › General's "Signature" (the editor on the left, the mail mock-up on the right, stacked below 1280 px),
 *  the editor with its placeholder chips, the mock-up and the filled signature itself (also the composer's "Added when sent"). */

/** the filled signature (shared's HTML: the short list of simple tags, every text escaped, the logo's picture, the filler's own colours);
 *  "No signature" when it is empty. Links open in a new tab here, without handing over the page (the mail needs neither). */
export function SignaturePreview({ html }: { html: string }) {
  if (!html) return <div className="ob-signature-preview ob-signature-preview--none">No signature</div>;
  return <div className="ob-signature-preview" dangerouslySetInnerHTML={{ __html: html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ') }} />;
}

/** the editor and the mock-up side by side from 1280 px, stacked below */
export function SignatureLayout({ children, mock }: { children: ReactNode; mock: ReactNode }) {
  return (
    <div className="ob-signature">
      <div className="ob-signature__edit">{children}</div>
      <div className="ob-signature__mock">{mock}</div>
    </div>
  );
}

/** a mail as the customer sees it, without a head since the signature HTML and role round (2026-10-28, the founder: the Sender pick names the
 *  sender, and Projects › General shows the From line under it): the message as placeholder lines, then the signature, and an optional link
 *  and note under the mock-up. A light "paper" in both themes, since the signature's colours are mail colours for white. */
export function MailMock({ html, link, note }: { html: string; /** e.g. "Edit the workspace signature" */ link?: ReactNode; note?: ReactNode }) {
  return (
    <div className="ob-mail-mock" role="group" aria-label="Mail preview">
      <div className="ob-mail-mock__body">
        <div className="ob-mail-mock__lines" aria-hidden="true"><span /><span /><span /></div>
        <SignaturePreview html={html} />
      </div>
      {link && <div className="ob-mail-mock__link">{link}</div>}
      {note && <p className="ob-mail-mock__note">{note}</p>}
    </div>
  );
}

/** the chips that insert a placeholder at the cursor of a text field (the signature's editor, the custom sender line); the field keeps focus */
export function PlaceholderChips({ field, value, onChange, max, chips, disabled }: { field: RefObject<HTMLTextAreaElement | HTMLInputElement | null>; value: string; onChange: (next: string) => void; max: number; chips: readonly string[]; disabled?: boolean }) {
  const insert = (chip: string) => {
    const el = field.current; if (!el) return;
    const at = el.selectionStart ?? value.length, end = el.selectionEnd ?? at;
    onChange(`${value.slice(0, at)}${chip}${value.slice(end)}`.slice(0, max));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(at + chip.length, at + chip.length); });
  };
  return (
    <div className="ob-signature__chips" role="group" aria-label="Placeholders">
      {chips.map((p) => <button key={p} type="button" className="ob-chip ob-signature__chip" disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={() => insert(p)}>{p}</button>)}
    </div>
  );
}

/** the signature's textarea (4,000 characters since 2026-10-28) and the placeholder chips; the help names the tags and the logo's sized forms */
export function SignatureEditor({ value, onChange, disabled }: { value: string; onChange: (next: string) => void; disabled?: boolean }) {
  const box = useRef<HTMLTextAreaElement>(null);
  return (
    <div className="ob-signature__editor">
      <textarea ref={box} className="ob-dialog__input ob-signature__text" value={value} maxLength={SIGNATURE_MAX} disabled={disabled} onChange={(e) => onChange(e.target.value)} aria-label="Signature" placeholder="Empty: replies end with your text" />
      <PlaceholderChips field={box} value={value} onChange={onChange} max={SIGNATURE_MAX} chips={SIGNATURE_PLACEHOLDERS} disabled={disabled} />
      <p className="ob-form-row__help ob-signature__help">Simple HTML works: p, br, small, strong, em, u, a, hr, lists and tables. Everything else is removed when saved.</p>
      <p className="ob-form-row__help ob-signature__help">{"{logo 160}"} sets the logo's width, {"{logo 160x60}"} its width and height.</p>
    </div>
  );
}

export type SignatureColorValues = { signatureColor: string | null; signatureLinkColor: string | null };
/** The signature's two colours (the signature HTML and role round, 2026-10-28; brief rule 3): the text presets are greys and black with the
 *  light grey default first, the link presets the palette with the workspace's accent first (its twin left out, so one swatch is checked);
 *  picking a default stores null, so the links keep following the accent. A short hint when the link colour is pale on white mail. */
export function SignatureColors({ value, accent: given, onChange, disabled }: { value: SignatureColorValues; accent: string; onChange: (patch: Partial<SignatureColorValues>) => void; disabled?: boolean }) {
  const accent = given.toLowerCase();
  const link = value.signatureLinkColor ?? accent;
  const linkPresets = [{ id: "accent", label: "Workspace accent", hex: accent }, ...ACCENTS.filter((a) => a.hex !== accent)];
  return (
    <>
      <FormRow label="Text colour" help="The signature's colour in the mail.">
        <AccentPicker label="Text colour" value={value.signatureColor ?? SIGNATURE_TEXT_COLOR} presets={[...SIGNATURE_TEXT_PRESETS]} disabled={disabled} onChange={(hex) => onChange({ signatureColor: hex === SIGNATURE_TEXT_COLOR ? null : hex })} />
      </FormRow>
      <FormRow label="Link colour" help={contrast(link, "#ffffff") < 4.5 ? "This colour is pale on white mail, so links may be hard to read." : "The colour of the signature's links; the workspace accent unless chosen."}>
        <AccentPicker label="Link colour" value={link} presets={linkPresets} disabled={disabled} onChange={(hex) => onChange({ signatureLinkColor: hex === accent ? null : hex })} />
      </FormRow>
    </>
  );
}
